// src/pages/SharedCardPage.tsx
//
// Public landing page for shared guide cards. URL: /guide/:shareId.
// Sits OUTSIDE ProtectedRoute — any visitor can see it whether or
// not they have an account.
//
// Reads /shared_cards/{shareId} (world-readable per Firestore rules).
// Renders card content with a CTA to open the app. If the recipient
// already has Instant Tours installed, the card opens via the SPA
// router naturally; otherwise the CTA leads to the home page.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Divider,
} from '@mui/material';
import {
  PlaceOutlined,
  EventOutlined,
  ListAltOutlined,
  HelpOutlineOutlined,
  AutoAwesomeOutlined,
  TipsAndUpdatesOutlined,
  HistoryEduOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Logo } from '../components/Logo';
import { useT } from '../i18n/useT';

interface SharedCardData {
  kind: string;
  title: string;
  body: string;
  meta?: string;
  place_name?: string;
  photo_url?: string;
  location?: { lat: number; lng: number };
  tags?: string[];
  list_items?: Array<{ title: string; blurb: string }>;
  for_city?: string;
  for_country?: string;
  /** Postcard-specific fields */
  visit_count?: number;
  most_recent_visit_at?: string;
}

const KIND_LABEL: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  place: { label: 'Place', icon: PlaceOutlined, color: 'rgb(124, 58, 237)' },
  event: { label: 'Event', icon: EventOutlined, color: 'rgb(220, 38, 38)' },
  list: { label: 'List', icon: ListAltOutlined, color: 'rgb(217, 119, 6)' },
  prompt: { label: 'Question', icon: HelpOutlineOutlined, color: 'rgb(37, 99, 235)' },
  suggestion: { label: 'Right now', icon: AutoAwesomeOutlined, color: 'rgb(5, 150, 105)' },
  tip: { label: 'Local tip', icon: TipsAndUpdatesOutlined, color: 'rgb(75, 85, 99)' },
  story: { label: 'Story', icon: HistoryEduOutlined, color: 'rgb(180, 83, 9)' },
  postcard: { label: 'Postcard', icon: PlaceOutlined, color: 'rgb(14, 165, 233)' },
};

export const SharedCardPage: React.FC = () => {
  const t = useT();
  const { shareId } = useParams<{ shareId: string }>();
  const [data, setData] = useState<SharedCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!shareId) {
      setLoading(false);
      setError('Invalid share link.');
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'shared_cards', shareId));
        if (cancelled) return;
        if (!snap.exists()) {
          setError('This card has expired or was removed.');
          setData(null);
        } else {
          setData(snap.data() as SharedCardData);
        }
      } catch (err) {
        console.warn('SharedCardPage fetch failed:', err);
        if (!cancelled) {
          setError("Couldn't load this card.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  // Build a Google Maps deep link for places when we have coords.
  const mapsUrl = useMemo(() => {
    if (!data?.location?.lat || !data?.location?.lng) return null;
    const q = encodeURIComponent(data.place_name || data.title);
    return `https://www.google.com/maps/search/?api=1&query=${q}&center=${data.location.lat},${data.location.lng}`;
  }, [data]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          bgcolor: 'white',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Logo size={24} alt={t("Instant Tours")} />
        <Box sx={{ flex: 1 }} />
        <Button
          component={RouterLink}
          to="/chat"
          size="small"
          variant="contained"
          sx={{ textTransform: 'none' }}
        >
          
          {t("Open the app")}
        </Button>
      </Box>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {loading && (
          <Stack spacing={1.5}>
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width="80%" height={32} />
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="70%" height={20} />
          </Stack>
        )}

        {!loading && error && (
          <Card variant="outlined" sx={{ borderRadius: 2, p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              
              {t("Open Instant Tours to discover places near you.")}
            </Typography>
            <Button
              component={RouterLink}
              to="/chat"
              variant="contained"
              sx={{ textTransform: 'none' }}
            >
              
              {t("Open Instant Tours")}
            </Button>
          </Card>
        )}

        {!loading && !error && data && <SharedCardContent data={data} mapsUrl={mapsUrl} />}
      </Container>
    </Box>
  );
};

const SharedCardContent: React.FC<{
  data: SharedCardData;
  mapsUrl: string | null;
}> = ({ data, mapsUrl }) => {
  const t = useT();
  const kindMeta = KIND_LABEL[data.kind] || KIND_LABEL.tip;
  const KindIcon = kindMeta.icon;
  const cityLine = [data.for_city, data.for_country].filter(Boolean).join(', ');

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {data.photo_url && (
        <Box
          component="img"
          src={data.photo_url}
          alt={data.title}
          sx={{
            width: '100%',
            height: 240,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      )}
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip
            icon={<KindIcon sx={{ fontSize: 14 }} />}
            label={kindMeta.label}
            size="small"
            sx={{
              height: 22,
              fontWeight: 600,
              fontSize: '0.7rem',
              color: kindMeta.color,
              bgcolor: 'transparent',
              border: 1,
              borderColor: kindMeta.color,
              '& .MuiChip-icon': { color: kindMeta.color, ml: '6px' },
            }}
          />
          {data.kind === 'postcard' && typeof data.visit_count === 'number' && data.visit_count > 1 && (
            <Chip
              label={`${data.visit_count} visits`}
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                fontSize: '0.7rem',
                bgcolor: 'rgba(14, 165, 233, 0.10)',
                color: 'rgb(14, 165, 233)',
              }}
            />
          )}
          {data.meta && (
            <Typography variant="caption" color="text.secondary">
              {data.meta}
            </Typography>
          )}
          {cityLine && (
            <Typography variant="caption" color="text.secondary">
              {cityLine}
            </Typography>
          )}
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.25, mb: 1 }}>
          {data.title}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
        >
          {data.body}
        </Typography>

        {data.list_items && data.list_items.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {data.list_items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  py: 1.25,
                  borderTop: idx === 0 ? 1 : 0,
                  borderBottom:
                    idx === (data.list_items?.length ?? 0) - 1 ? 0 : 1,
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    minWidth: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'rgba(217, 119, 6, 0.10)',
                    color: 'rgb(217, 119, 6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  {idx + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                  {item.blurb && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, lineHeight: 1.45 }}
                    >
                      {item.blurb}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {data.tags && data.tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 2, flexWrap: 'wrap' }}>
            {data.tags.slice(0, 6).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            component={RouterLink}
            to="/chat"
            variant="contained"
            sx={{ textTransform: 'none', flex: '1 1 200px' }}
          >
            
            {t("Open in Instant Tours")}
          </Button>
          {mapsUrl && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewOutlined />}
              onClick={() => window.open(mapsUrl, '_blank', 'noopener')}
              sx={{ textTransform: 'none' }}
            >
              
              {t("Open in Maps")}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
